/**
 * Premium Icon Container Adapter
 * 
 * This adapter wraps the base IconContainer component to provide
 * premium styling and effects consistent with our design system.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { IconContainer as BaseIconContainer } from '../icon-container';

export interface IconContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The content to be displayed inside the container
   */
  children: React.ReactNode;

  /**
   * Shape of the container
   */
  shape?: 'square' | 'rounded' | 'circle';
}

/**
 * Premium Icon Container
 * 
 * Enhanced icon container with premium styling, animations and effects,
 * consistent with the design system.
 * 
 * @example
 * ```jsx
 * <IconContainer shape="circle">
 *   <IconComponent />
 * </IconContainer>
 * ```
 */
const IconContainer = React.forwardRef<HTMLDivElement, IconContainerProps>(
  ({ 
    className, 
    children,
    shape = 'rounded',
    ...props 
  }, ref) => {
    // Size classes
    const sizeClasses = "h-14 w-14";
    
    // Variant classes
    const variantClasses = "premium-gradient-primary text-white";
    
    // Shadow classes
    const shadowClasses = "shadow-[0_4px_12px_-4px_rgba(255,82,82,0.25)] dark:shadow-[0_8px_20px_-4px_rgba(255,82,82,0.3)]";
    
    // Shape variants
    const shapeVariants = {
      'square': "",
      'rounded': "premium-radius-md",
      'circle': "premium-radius-full"
    };
    
    return (
      <div className="relative mb-6">
        <BaseIconContainer
          ref={ref}
          className={cn(
            "relative",
            sizeClasses,
            variantClasses,
            shadowClasses,
            shapeVariants[shape],
            className
          )}
          shape={shape}
          {...props}
        >
          {children}
        </BaseIconContainer>
        
        {/* Glow effect */}
        <div 
          className={cn(
            "absolute -inset-0.5 premium-radius-xl blur-md -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700",
            "bg-primary-500/20 dark:bg-primary-500/40"
          )}
        />
      </div>
    );
  }
);

IconContainer.displayName = "IconContainer";

export { IconContainer }; 
